import type { ComponentConfig } from "@puckeditor/core";
import { BrandMarquee, type BrandMarqueeProps } from "./brand-marquee";
import { BrandMarqueeFields } from "./brand-marquee.fields";
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { ImagePicker } from "../editor/ImagePicker";

export const BrandMarqueeConfig: ComponentConfig<BrandMarqueeProps> = {
  label: "Brand Marquee",
  fields: {
    heading: {
      ...BrandMarqueeFields.heading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="brandMarquee.heading"
          label="Heading"
          multiline={false}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheading: {
      ...BrandMarqueeFields.subheading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="brandMarquee.subheading"
          label="Subheading"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    displayMode: BrandMarqueeFields.displayMode,
    mediaStyle: BrandMarqueeFields.mediaStyle,
    scrolling: BrandMarqueeFields.scrolling,
    spacing: BrandMarqueeFields.spacing,
    chipStyle: BrandMarqueeFields.chipStyle,
    shadow: BrandMarqueeFields.shadow,
    brands: {
      ...BrandMarqueeFields.brands,
      arrayFields: {
        ...BrandMarqueeFields.brands.arrayFields,
        logoUrl: {
          ...BrandMarqueeFields.brands.arrayFields.logoUrl,
          render: ({
            value,
            onChange,
          }: {
            value?: string;
            onChange: (val: string | undefined) => void;
          }) => <ImagePicker value={value || ""} onChange={onChange} label="Logo" />,
        },
      },
    } as any,
    backgroundColor: BrandMarqueeFields.backgroundColor,
  },
  /**
   * The shadow only lands on a chip surface, and a logo URL is never read when brands render
   * as names.
   */
  resolveFields: (data, { fields }) => {
    const props = (data.props || {}) as { chipStyle?: string; displayMode?: string };
    const visible = { ...(fields as Record<string, any>) };

    if (props.chipStyle === "none") {
      delete visible.shadow;
    }

    if (props.displayMode === "names" && visible.brands?.arrayFields?.logoUrl) {
      const { logoUrl: _logoUrl, ...arrayFields } = visible.brands.arrayFields;
      visible.brands = { ...visible.brands, arrayFields };
    }

    // Image style only changes how uploaded images render.
    if (props.displayMode === "names") {
      delete visible.mediaStyle;
    }

    return visible as typeof fields;
  },
  defaultProps: {
    heading: "Brands We Work With",
    subheading: "Trusted products from the manufacturers we know best.",
    displayMode: "mixed",
    mediaStyle: "logo",
    scrolling: "yes",
    spacing: "default",
    chipStyle: "pill",
    shadow: "yes",
    brands: [
      { name: "Brand 1", logoUrl: "", href: "" },
      { name: "Brand 2", logoUrl: "", href: "" },
      { name: "Brand 3", logoUrl: "", href: "" },
      { name: "Brand 4", logoUrl: "", href: "" },
      { name: "Brand 5", logoUrl: "", href: "" },
      { name: "Brand 6", logoUrl: "", href: "" },
    ],
  },
  render: ({ id, puck: _puck, ...props }) => <BrandMarquee id={id} {...props} />,
};
